import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useAuthStore } from '../../store/AuthContext';
import { useAppStore } from '../../store/AppContext';
import NoData from '../../components/NoData';
import { PriceFormatter } from '../../utils/PriceFormatter';
import { SaldoData } from '../../types/Types';

const PAGE_SIZE = 10;

type Props = {
  data: SaldoData[];
};

const CustomPaginationTable: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const dataUser = useAuthStore();
  const dataApp = useAppStore();
  const { moneda } = dataUser.user;
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstRowIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleData = data.slice(firstRowIndex, firstRowIndex + PAGE_SIZE);

  useEffect(() => {
    setPage((previousPage) => Math.min(previousPage, pageCount));
  }, [pageCount]);

  const priceFormatter = (cell: SaldoData['importe'] | string) => {
    const monedaText = moneda === 'euro' ? t('euro') : t('cc');
    return PriceFormatter.formatStringAsPrice(cell.toString(), monedaText);
  };

  const dateFormatter = (cell: string) => {
    if (dataApp.lang === 'eu') {
      return moment(cell, 'DD/MM/YYYY').format('YYYY/MM/DD');
    }
    return cell;
  };

  const columnClassNameFormat = (fieldValue: SaldoData['importe'] | string) => {
    return !fieldValue.toString().startsWith('-')
      ? 'td-importe-saldo td-importe-saldo-positivo'
      : 'td-importe-saldo ';
  };

  if (data.length === 0) {
    return (
      <NoData>
        <div className="mensaje">
          <p>{t('saldo.noRecords')}</p>
        </div>
      </NoData>
    );
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table saldo-table">
          <caption className="sr-only">{t('saldo.title')}</caption>
          <thead>
            <tr>
              <th className="th-fecha-saldo" scope="col">
                {t('saldo.date')}
              </th>
              <th scope="col">{t('saldo.concept')}</th>
              <th className="th-importe-saldo" scope="col">
                {t('saldo.amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleData.map((movement, index) => (
              <tr key={`saldo-movement-${firstRowIndex + index}`}>
                <td className="td-fecha-saldo">
                  {dateFormatter(movement.fecha)}
                </td>
                <td>{movement.concepto}</td>
                <td className={columnClassNameFormat(movement.importe)}>
                  {priceFormatter(movement.importe)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <nav aria-label={t('saldo.pagination')}>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                {t('saldo.previousPage')}
              </button>
            </li>
            {Array.from({ length: pageCount }, (_, index) => index + 1).map(
              (pageNumber) => (
                <li
                  className={`page-item ${
                    pageNumber === currentPage ? 'active' : ''
                  }`}
                  key={pageNumber}
                >
                  <button
                    type="button"
                    className="page-link"
                    aria-current={
                      pageNumber === currentPage ? 'page' : undefined
                    }
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                </li>
              ),
            )}
            <li
              className={`page-item ${
                currentPage === pageCount ? 'disabled' : ''
              }`}
            >
              <button
                type="button"
                className="page-link"
                disabled={currentPage === pageCount}
                onClick={() => setPage(currentPage + 1)}
              >
                {t('saldo.nextPage')}
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default CustomPaginationTable;
